/**
 * Invite Actions Hook
 * Mutation actions for admin invite management.
 */

import { apiUrl } from '@config/api';
import { useAuth } from '@contexts/AuthContext';
import { useCallback, useState } from 'react';

export interface SendInviteResult {
  success: boolean;
  error?: string;
  invite?: { code: string; email: string };
}

export interface UseInviteActionsResult {
  sendInvite: (email: string, role?: string) => Promise<SendInviteResult>;
  revokeInvite: (id: number) => Promise<{ success: boolean; error?: string }>;
  resendInvite: (id: number) => Promise<{ success: boolean; error?: string }>;
  loading: boolean;
}

export function useInviteActions(): UseInviteActionsResult {
  const { authFetch } = useAuth();
  const [loading, setLoading] = useState(false);

  const sendInvite = useCallback(
    async (email: string, role?: string) => {
      setLoading(true);
      try {
        const res = await authFetch(apiUrl('/api/admin/invites'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, role }),
        });
        const data = await res.json();
        if (!res.ok) {
          return { success: false, error: data.error || 'Failed to send invite' };
        }
        return {
          success: true,
          invite: data.invite ? { code: data.invite.code, email: data.invite.email } : undefined,
        };
      } catch (err) {
        return { success: false, error: (err as Error).message };
      } finally {
        setLoading(false);
      }
    },
    [authFetch],
  );

  const revokeInvite = useCallback(
    async (id: number) => {
      setLoading(true);
      try {
        const res = await authFetch(apiUrl(`/api/admin/invites/${id}`), {
          method: 'DELETE',
        });
        const data = await res.json();
        if (!res.ok) {
          return { success: false, error: data.error || 'Failed to revoke invite' };
        }
        return { success: true };
      } catch (err) {
        return { success: false, error: (err as Error).message };
      } finally {
        setLoading(false);
      }
    },
    [authFetch],
  );

  const resendInvite = useCallback(
    async (id: number) => {
      setLoading(true);
      try {
        const res = await authFetch(apiUrl(`/api/admin/invites/${id}/resend`), {
          method: 'POST',
        });
        const data = await res.json();
        if (!res.ok) {
          return { success: false, error: data.error || 'Failed to resend invite' };
        }
        return { success: true };
      } catch (err) {
        return { success: false, error: (err as Error).message };
      } finally {
        setLoading(false);
      }
    },
    [authFetch],
  );

  return { sendInvite, revokeInvite, resendInvite, loading };
}
